import React from "react";
import MapInput from "../../../components/Booking/MapInput";
import Map from "../../../components/Booking/Map";
import BookingMedium from "../../../components/Booking/BookingMedium";
import Counter from "../../../components/Booking/Counter";
import MapModal from "../../../components/Booking/MapModal";
import { useAtom } from "jotai";
import {
  isMapShownAtom,
  isMinimizedAtom,
} from "../../../jotai-store/isMapShow";
import LabourToggle from "../../../components/Booking/LabourToggle";
import NotATruckModal from "../../../components/Booking/NotATruckModal";

const LocationScreen = (props) => {
  const {
    stops,
    type,
    pickupLocation,
    dropoffLocation,
    handleUpdateBookingObject,
    pickupLatitude,
    pickupLongitude,
    dropoffLatitude,
    dropoffLongitude,
    distanceInKm,
    durationInMins,
    pickupMedium,
    dropoffMedium,
    pickupNoOfStairs,
    dropoffNoOfStairs,
    pickupNoOfFloors,
    dropoffNoOfFloors,
    isTruckRequired,
    isLocationRouteComputing,
    onRouteCalculationStatusChange,
  } = props;

  const [isMapShown, setIsMapShown] = useAtom(isMapShownAtom);
  const [isMinimized, setIsMinimized] = useAtom(isMinimizedAtom);
  const [isNotATruckModalOpen, setIsNotATruckModalOpen] = React.useState(false);

  const clamp = (num, min, max) => Math.min(max, Math.max(min, num));

  // Helpers to update stop-specific medium and counters
  const handleStopMediumChange = (index, medium) => {
    try {
      const newStops = stops ? [...stops] : [];
      const existing = newStops[index] || {};
      const updated = { ...existing, stopMedium: medium };

      const m = (medium || "").toString().toLowerCase();
      // Rules: mainFloor -> stopTotalFloors = 0; stairs/elevator -> >=1
      if (m === "stairs" || m === "elevator") {
        const prev = existing?.stopTotalFloors;
        updated.stopTotalFloors = typeof prev === "number" && prev > 0 ? 1 : 1;
      } else {
        updated.stopTotalFloors = 0;
      }

      // Remove deprecated fields if present
      if (updated.hasOwnProperty("noOfFloors")) delete updated.noOfFloors;
      if (updated.hasOwnProperty("noOfStairs")) delete updated.noOfStairs;
      newStops[index] = updated;
      handleUpdateBookingObject("stops", newStops, `stop.medium.${index}`);
    } catch (error) {
      console.log(error);
    }
  };

  const updateStopCounter = (index, value, tag) => {
    try {
      const newStops = stops ? [...stops] : [];
      newStops[index] = { ...newStops[index], stopTotalFloors: value };
      handleUpdateBookingObject("stops", newStops, tag);
    } catch (error) {
      console.log(error);
    }
  };

  const incrementStopTotalFloors = (index) => {
    const val = (stops?.[index]?.stopTotalFloors ?? 1) + 1;
    updateStopCounter(index, val, `stop.stopTotalFloors.inc.${index}`);
  };

  const decrementStopTotalFloors = (index) => {
    const current = stops?.[index]?.stopTotalFloors ?? 1;
    // Min 1 for stairs/elevator, but caller will only show counter for those media
    const val = Math.max(1, current - 1);
    updateStopCounter(index, val, `stop.stopTotalFloors.dec.${index}`);
  };

  // Determine whether counters for pickup/dropoff are visible
  const showPickupCounter =
    pickupMedium === "stairs" || pickupMedium === "elevator";
  const showDropoffCounter =
    dropoffMedium === "stairs" || dropoffMedium === "elevator";
  const bothCountersVisible = showPickupCounter && showDropoffCounter;

  if (isMapShown) {
    return (
      <div>
        <div>
          <Map
            hide={false}
            pickupLocation={pickupLocation}
            dropoffLocation={dropoffLocation}
            stops={stops}
            pickupLatitude={pickupLatitude}
            pickupLongitude={pickupLongitude}
            dropoffLatitude={dropoffLatitude}
            dropoffLongitude={dropoffLongitude}
            distanceInKm={distanceInKm}
            durationInMins={durationInMins}
            handleUpdateBookingObject={handleUpdateBookingObject}
            mapHeight={isMinimized ? "100%" : "calc(50vh - 60px)"}
            onRouteCalculationStatusChange={onRouteCalculationStatusChange}

            // isfullscreen={true}
          />
        </div>

        <div>
          <MapModal
            isOpen={true}
            handleUpdateBookingObject={handleUpdateBookingObject}
            pickupLocation={pickupLocation}
            dropoffLocation={dropoffLocation}
            isTruckRequired={isTruckRequired}
            isLocationRouteComputing={isLocationRouteComputing}
          >
            <div className="flex flex-col">
              <MapInput
                stops={stops}
                type={type}
                pickupLocation={pickupLocation}
                dropoffLocation={dropoffLocation}
                handleUpdateBookingObject={handleUpdateBookingObject}
                title={""}
                isTruckRequired={isTruckRequired}
              />
            </div>

            <LabourToggle
              setIsNotATruckModalOpen={setIsNotATruckModalOpen}
              isNotATruckModalOpen={isNotATruckModalOpen}
              isTruckRequired={isTruckRequired}
              handleUpdateBookingObject={handleUpdateBookingObject}
            />
          </MapModal>
        </div>
        <NotATruckModal
          isNotATruckModalOpen={isNotATruckModalOpen}
          onClose={() => setIsNotATruckModalOpen(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="w-full px-[33px] overflow-y-auto max-w-container">
        <div
          className="text-[#211F59] font-instrument text-[22px] font-semibold mb-[16px]"
          style={{ lineHeight: "23px" }}
        >
          Book your move in minutes
        </div>

        <MapInput
          stops={stops}
          type={type}
          pickupLocation={pickupLocation}
          isTruckRequired={isTruckRequired}
          dropoffLocation={dropoffLocation}
          handleUpdateBookingObject={handleUpdateBookingObject}
          title={""}
          renderAfterPickup={() => (
            <>
              <BookingMedium
                label="From"
                location={pickupLocation}
                medium={pickupMedium}
                clickFunction={(item) =>
                  handleUpdateBookingObject("pickupMedium", item, "22.1")
                }
              />
              {pickupMedium === "stairs" ? (
                <Counter
                  value={pickupNoOfStairs}
                  label={"Floor number (0 = main floor)"}
                  max={5}
                  min={1}
                  type={type}
                  onValueChange={(next) =>
                    handleUpdateBookingObject(
                      "pickupNoOfStairs",
                      clamp(next, 1, 5),
                      "25.1.manual",
                    )
                  }
                  increment={() =>
                    handleUpdateBookingObject(
                      "pickupNoOfStairs",
                      pickupNoOfStairs + 1,
                      "25.1",
                    )
                  }
                  decrement={() =>
                    handleUpdateBookingObject(
                      "pickupNoOfStairs",
                      Math.max(1, pickupNoOfStairs - 1),
                      "25.2",
                    )
                  }
                />
              ) : pickupMedium === "elevator" ? (
                <Counter
                  value={pickupNoOfFloors}
                  label={"Floor number (0 = main floor)"}
                  max={60}
                  min={1}
                  type={type}
                  onValueChange={(next) =>
                    handleUpdateBookingObject(
                      "pickupNoOfFloors",
                      clamp(next, 1, 60),
                      "25.5.manual",
                    )
                  }
                  increment={() =>
                    handleUpdateBookingObject(
                      "pickupNoOfFloors",
                      pickupNoOfFloors + 1,
                      "25.5",
                    )
                  }
                  decrement={() =>
                    handleUpdateBookingObject(
                      "pickupNoOfFloors",
                      Math.max(1, pickupNoOfFloors - 1),
                      "25.6",
                    )
                  }
                />
              ) : null}
            </>
          )}
          renderAfterStop={(idx) => (
            <>
              <BookingMedium
                label={`Stop #${idx + 1}`}
                location={stops?.[idx]?.stop}
                medium={stops?.[idx]?.stopMedium}
                clickFunction={(item) => handleStopMediumChange(idx, item)}
                maxLocationLength={36}
              />
              {stops?.[idx]?.stopMedium === "stairs" ||
              stops?.[idx]?.stopMedium === "elevator" ? (
                <Counter
                  value={stops?.[idx]?.stopTotalFloors}
                  label={"Floor number (0 = main floor)"}
                  max={stops?.[idx]?.stopMedium === "elevator" ? 60 : 5}
                  min={1}
                  type={type}
                  onValueChange={(next) =>
                    updateStopCounter(
                      idx,
                      clamp(
                        next,
                        1,
                        stops?.[idx]?.stopMedium === "elevator" ? 60 : 5,
                      ),
                      `stop.stopTotalFloors.manual.${idx}`,
                    )
                  }
                  increment={() => incrementStopTotalFloors(idx)}
                  decrement={() => decrementStopTotalFloors(idx)}
                />
              ) : null}
            </>
          )}
          renderAfterDropoff={() => (
            <>
              <BookingMedium
                label="To"
                location={dropoffLocation}
                medium={dropoffMedium}
                maxLocationLength={36}
                clickFunction={(item) =>
                  handleUpdateBookingObject("dropoffMedium", item, "22.2")
                }
              />
              {dropoffMedium === "stairs" ? (
                <Counter
                  value={dropoffNoOfStairs}
                  label={"Floor number (0 = main floor)"}
                  max={5}
                  min={1}
                  type={type}
                  onValueChange={(next) =>
                    handleUpdateBookingObject(
                      "dropoffNoOfStairs",
                      clamp(next, 1, 5),
                      "25.3.manual",
                    )
                  }
                  increment={() =>
                    handleUpdateBookingObject(
                      "dropoffNoOfStairs",
                      dropoffNoOfStairs + 1,
                      "25.3",
                    )
                  }
                  decrement={() =>
                    handleUpdateBookingObject(
                      "dropoffNoOfStairs",
                      Math.max(1, dropoffNoOfStairs - 1),
                      "25.4",
                    )
                  }
                />
              ) : dropoffMedium === "elevator" ? (
                <Counter
                  value={dropoffNoOfFloors}
                  label={"Floor number (0 = main floor)"}
                  max={60}
                  min={1}
                  type={type}
                  onValueChange={(next) =>
                    handleUpdateBookingObject(
                      "dropoffNoOfFloors",
                      clamp(next, 1, 60),
                      "25.7.manual",
                    )
                  }
                  increment={() =>
                    handleUpdateBookingObject(
                      "dropoffNoOfFloors",
                      dropoffNoOfFloors + 1,
                      "25.7",
                    )
                  }
                  decrement={() =>
                    handleUpdateBookingObject(
                      "dropoffNoOfFloors",
                      Math.max(1, dropoffNoOfFloors - 1),
                      "25.8",
                    )
                  }
                />
              ) : null}
            </>
          )}
        />
        <Map
          hide={true}
          pickupLocation={pickupLocation}
          dropoffLocation={dropoffLocation}
          stops={stops}
          pickupLatitude={pickupLatitude}
          pickupLongitude={pickupLongitude}
          dropoffLatitude={dropoffLatitude}
          dropoffLongitude={dropoffLongitude}
          distanceInKm={distanceInKm}
          durationInMins={durationInMins}
          handleUpdateBookingObject={handleUpdateBookingObject}
          onRouteCalculationStatusChange={onRouteCalculationStatusChange}
        />

        {/* Mediums and counters are rendered inline by MapInput via render props */}
      </div>
    </div>
  );
};

export default LocationScreen;
